/**
 * Select - IBKD规范选择框组件
 *
 * 使用自定义下拉面板（白底、边框、阴影），避免原生 <select> 在各系统上灰底/不可控样式。
 */
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { Check, ChevronDown } from 'lucide-react'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'size'> {
  label?: string
  error?: string
  hint?: string
  options: SelectOption[]
  placeholder?: string
}

function nextEnabledIndex(opts: SelectOption[], from: number, delta: number): number {
  const n = opts.length
  if (n === 0) return -1
  let i = from
  for (let step = 0; step < n; step++) {
    i = (i + delta + n) % n
    if (!opts[i]?.disabled) return i
  }
  return from
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      label,
      error,
      hint,
      options,
      placeholder,
      className,
      disabled,
      value: valueProp,
      defaultValue,
      onChange,
      id: idProp,
      name,
      required,
      autoFocus,
      onBlur,
      onFocus,
      onKeyDown: onKeyDownProp,
    },
    ref
  ) => {
    const autoId = useId()
    const listboxId = `${autoId}-listbox`
    const labelId = `${autoId}-label`
    const buttonId = idProp ?? `${autoId}-trigger`

    const mergedOptions = useMemo(() => {
      if (!placeholder?.trim()) return options
      if (options.some((o) => o.value === '')) return options
      return [{ value: '', label: placeholder }, ...options]
    }, [options, placeholder])

    const isControlled = valueProp !== undefined
    const [uncontrolled, setUncontrolled] = useState(() => (defaultValue != null ? String(defaultValue) : ''))
    const value = isControlled ? String(valueProp ?? '') : uncontrolled

    const [open, setOpen] = useState(false)
    const [highlightIndex, setHighlightIndex] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)

    const selectedLabel = useMemo(() => {
      const hit = mergedOptions.find((o) => o.value === value)
      return hit?.label ?? ''
    }, [mergedOptions, value])

    const emitChange = useCallback(
      (next: string) => {
        if (!isControlled) setUncontrolled(next)
        if (onChange) {
          const el = document.createElement('select')
          Object.defineProperty(el, 'value', { value: next, configurable: true })
          onChange({
            target: el,
            currentTarget: el,
          } as React.ChangeEvent<HTMLSelectElement>)
        }
      },
      [isControlled, onChange]
    )

    const pickIndex = useCallback(
      (index: number) => {
        const opt = mergedOptions[index]
        if (!opt || opt.disabled) return
        emitChange(opt.value)
        setOpen(false)
      },
      [emitChange, mergedOptions]
    )

    const openMenu = useCallback(() => {
      const sel = mergedOptions.findIndex((o) => o.value === value && !o.disabled)
      const start = sel >= 0 ? sel : nextEnabledIndex(mergedOptions, 0, 1)
      setHighlightIndex(start >= 0 ? start : 0)
      setOpen(true)
    }, [mergedOptions, value])

    useEffect(() => {
      if (!open) return
      const onDoc = (e: MouseEvent) => {
        if (!containerRef.current?.contains(e.target as Node)) {
          setOpen(false)
        }
      }
      document.addEventListener('mousedown', onDoc, true)
      return () => document.removeEventListener('mousedown', onDoc, true)
    }, [open])

    const onButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      onKeyDownProp?.(e as unknown as React.KeyboardEvent<HTMLSelectElement>)
      if (e.defaultPrevented || disabled) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (open) pickIndex(highlightIndex)
        else openMenu()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!open) openMenu()
        else setHighlightIndex((hi) => nextEnabledIndex(mergedOptions, hi, 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (!open) openMenu()
        else setHighlightIndex((hi) => nextEnabledIndex(mergedOptions, hi, -1))
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
      }
    }

    const onListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightIndex((hi) => nextEnabledIndex(mergedOptions, hi, 1))
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightIndex((hi) => nextEnabledIndex(mergedOptions, hi, -1))
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        pickIndex(highlightIndex)
      }
    }

    return (
      <div className="w-full">
        {label ? (
          <label id={labelId} className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor={buttonId}>
            {label}
          </label>
        ) : null}
        <div className="relative" ref={containerRef}>
          {name != null && name !== '' ? <input type="hidden" name={name} value={value} readOnly /> : null}
          <button
            ref={ref}
            type="button"
            id={buttonId}
            disabled={disabled}
            autoFocus={autoFocus}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-labelledby={label ? labelId : undefined}
            aria-required={required}
            className={clsx(
              'w-full h-10 px-3 pr-10 rounded-lg border bg-white text-left',
              'text-sm text-slate-800',
              'transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
              'disabled:bg-slate-100 disabled:cursor-not-allowed',
              error
                ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20'
                : 'border-slate-300',
              className
            )}
            onBlur={onBlur ? (e) => onBlur(e as unknown as React.FocusEvent<HTMLSelectElement>) : undefined}
            onFocus={onFocus ? (e) => onFocus(e as unknown as React.FocusEvent<HTMLSelectElement>) : undefined}
            onClick={() => {
              if (disabled) return
              if (open) setOpen(false)
              else openMenu()
            }}
            onKeyDown={onButtonKeyDown}
          >
            <span className="block truncate">{selectedLabel || '—'}</span>
          </button>
          <ChevronDown
            className={clsx(
              'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none transition-transform',
              open && 'rotate-180'
            )}
            aria-hidden
          />

          {open && !disabled ? (
            <ul
              id={listboxId}
              role="listbox"
              tabIndex={-1}
              onKeyDown={onListKeyDown}
              className={clsx(
                'absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg',
                'ring-1 ring-black/5'
              )}
            >
              {mergedOptions.map((option, index) => {
                const selected = option.value === value
                const hi = highlightIndex === index
                return (
                  <li
                    key={`${option.value}-${index}`}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={option.disabled}
                    className={clsx(
                      'px-3 py-2 text-sm flex items-center justify-between gap-2',
                      option.disabled ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 cursor-pointer',
                      !option.disabled && hi && 'bg-slate-50',
                      !option.disabled && selected && 'bg-primary-50 text-primary-900',
                      !option.disabled && !selected && 'hover:bg-slate-50'
                    )}
                    onMouseEnter={() => {
                      if (!option.disabled) setHighlightIndex(index)
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (!option.disabled) pickIndex(index)
                    }}
                  >
                    <span className="truncate">{option.label}</span>
                    {selected ? <Check className="w-4 h-4 shrink-0 text-primary-600" aria-hidden /> : null}
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
        {error && <p className="mt-1.5 text-sm text-error-500">{error}</p>}
        {hint && !error && <p className="mt-1.5 text-sm text-slate-500">{hint}</p>}
      </div>
    )
  }
)

Select.displayName = 'Select'
